<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSessionStore } from '@/store/sessionStore'
import axiosInstance from '@/plugins/axios'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import SettingsPageTemplate from '@/components/templates/SettingsPageTemplate.vue'

const { t } = useI18n()

const sessionStore = useSessionStore()
const translations = ref<any[]>([])
const isLoading = ref(true)
const themeName = ref('')

const tenant = computed(() => sessionStore.getTenant() || {
  id: '',
  name: '',
  domains: [],
  settings: { defaultLanguage: '' },
  adminSettings: { enabledLanguages: [] },
  theme: null,
  mongoDBisConnected: false,
})

const getLanguageIdentifierById = (id: string) => {
  const t = translations.value.find((l) => l._id === id)
  return t?.languageIdentifier ?? id
}

onMounted(async () => {
  try {
    const results = await Promise.all(
      tenant.value.adminSettings.enabledLanguages.map((lang: string) =>
        axiosInstance.get(`/translations/${lang}`).then((r) => r.data)
      )
    )
    translations.value = results

    if (tenant.value.theme) {
      const res = await axiosInstance.get(`/theme/themeNameById/${tenant.value.id}`)
      themeName.value = res.data.name
    }
  } finally {
    isLoading.value = false
  }
})
</script>

<template>
  <SettingsPageTemplate :showSave="false" :showCancel="false">
    <template #settings-content>
      <div class="info-grid">

        <div class="info-row">
          <span class="info-icon"><FontAwesomeIcon icon="building" /></span>
          <span class="info-label">{{ t('settings.tenantInfo.name') }}</span>
          <span class="info-value">{{ tenant.name || '–' }}</span>
        </div>

        <div class="info-row">
          <span class="info-icon"><FontAwesomeIcon icon="globe" /></span>
          <span class="info-label">{{ t('settings.tenantInfo.domains') }}</span>
          <div class="info-value info-value--tags">
            <Tag
              v-for="domain in tenant.domains"
              :key="domain"
              severity="secondary"
              class="domain-tag"
            >{{ domain }}</Tag>
            <span v-if="!tenant.domains?.length" class="empty">–</span>
          </div>
        </div>

        <div class="info-row">
          <span class="info-icon"><FontAwesomeIcon icon="language" /></span>
          <span class="info-label">{{ t('settings.tenantInfo.enabledLanguages') }}</span>
          <div class="info-value info-value--tags">
            <template v-if="isLoading">
              <Skeleton width="6rem" height="1.5rem" />
            </template>
            <template v-else>
              <Tag
                v-for="lang in tenant.adminSettings.enabledLanguages"
                :key="lang"
                severity="secondary"
              >{{ getLanguageIdentifierById(lang) }}</Tag>
              <span v-if="!tenant.adminSettings.enabledLanguages?.length" class="empty">–</span>
            </template>
          </div>
        </div>

        <div class="info-row">
          <span class="info-icon"><FontAwesomeIcon icon="flag" /></span>
          <span class="info-label">{{ t('settings.tenantInfo.defaultLanguage') }}</span>
          <span class="info-value">
            <Skeleton v-if="isLoading" width="6rem" height="1.5rem" />
            <Tag v-else-if="tenant.settings?.defaultLanguage" severity="primary">
              {{ getLanguageIdentifierById(tenant.settings.defaultLanguage) }}
            </Tag>
            <span v-else class="empty">–</span>
          </span>
        </div>

        <div class="info-row">
          <span class="info-icon"><FontAwesomeIcon icon="palette" /></span>
          <span class="info-label">{{ t('settings.tenantInfo.theme') }}</span>
          <span class="info-value">
            <Skeleton v-if="isLoading" width="8rem" height="1.5rem" />
            <Tag v-else-if="tenant.theme" severity="success">
              <template #icon><FontAwesomeIcon icon="circle-check" class="mr-1" /></template>
              {{ themeName }}
            </Tag>
            <Tag v-else severity="warn">
              <template #icon><FontAwesomeIcon icon="triangle-exclamation" class="mr-1" /></template>
              {{ t('settings.tenantInfo.noTheme') }}
            </Tag>
          </span>
        </div>

        <div class="info-row">
          <span class="info-icon"><FontAwesomeIcon icon="database" /></span>
          <span class="info-label">{{ t('settings.tenantInfo.reportDatabase') }}</span>
          <span class="info-value">
            <Skeleton v-if="isLoading" width="10rem" height="1.5rem" />
            <Tag v-else-if="tenant.mongoDBisConnected" severity="success">
              <template #icon><FontAwesomeIcon icon="circle-check" class="mr-1" /></template>
              {{ t('settings.tenantInfo.dbConnected') }}
            </Tag>
            <Tag v-else severity="warn">
              <template #icon><FontAwesomeIcon icon="triangle-exclamation" class="mr-1" /></template>
              {{ t('settings.tenantInfo.dbNotConnected') }}
            </Tag>
          </span>
        </div>

      </div>
    </template>
  </SettingsPageTemplate>
</template>

<style scoped>
.info-grid {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.info-row {
  display: grid;
  grid-template-columns: 2rem 12rem 1fr;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 0.25rem;
  border-bottom: 1px solid var(--surface-border, #e5e7eb);
}

.info-row:last-child {
  border-bottom: none;
}

.info-icon {
  color: var(--p-primary-500);
  font-size: 0.95rem;
  display: flex;
  justify-content: center;
}

.info-label {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--p-surface-600);
}

.info-value {
  font-size: 0.9rem;
  color: var(--p-surface-800);
}

.info-value--tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
}

.domain-tag {
  font-family: monospace;
  font-size: 0.8rem;
}

.empty {
  color: var(--p-surface-400);
}
</style>
